/**
 * Locale Editor Main Entry Point
 * 
 * This is the full TypeScript implementation of the locale editor.
 * It replaces the inline JavaScript that was in editor.html.
 */

import * as Phaser from 'phaser';
import * as jsyaml from 'js-yaml';

// Import types from centralized types module
import type {
  PropData,
  PropRegistryEntry,
  PropPack,
  SpriteRegistryEntry,
  SpritePack,
  Position,
  LocaleData,
  HostMessage,
} from './types';

// Import EditorState for centralized state management
import { EditorState, type StateSnapshot } from './EditorState';

// Import EditorScene (extracted to separate file)
import { EditorScene, type EditorContext } from './EditorScene';

// Import DOM utilities (prevent null reference errors in embedded mode)
import { setDisplay, setText, setValue, setChecked } from './dom-utils';

// ============================================
// DEMO MODE DETECTION
// ============================================
// Check if running in demo/production mode (no backend save available)
function isDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  // Demo mode on Vercel, pixelgame domain, or any non-localhost
  return hostname.includes('vercel.app') || 
         hostname.includes('pixelgame') ||
         (hostname !== 'localhost' && hostname !== '127.0.0.1');
}

// ============================================
// PROP REGISTRY (loaded from props.json)
// ============================================
let PROP_PACKS: PropPack[] = [];
let PROP_REGISTRY: PropRegistryEntry[] = [];
let currentPackFilter = 'all';

// ============================================
// SPRITE REGISTRY (loaded from sprites.json)
// ============================================
let SPRITE_PACKS: SpritePack[] = [];
let SPRITE_REGISTRY: SpriteRegistryEntry[] = [];

// Fallback categories for organizing by type
const CATEGORY_MAPPINGS: Record<string, string[]> = {
  'Windows': ['window'],
  'Curtains': ['curtain'],
  'Doors': ['door'],
  'Tables': ['table'],
  'Desks': ['desk'],
  'Seating': ['stool', 'bench', 'chair'],
  'Storage': ['chest', 'box', 'cupboard', 'drawer', 'shelf'],
  'Carpets': ['carpet'],
  'Plants': ['plant', 'flower'],
  'Bottles': ['bottle', 'potion', 'jug', 'pot', 'goblet'],
  'Lighting': ['candle'],
  'Wall Decor': ['poster', 'board', 'clock', 'tv'],
  'Stairs': ['stairs'],
  'Fireplace': ['furnace', 'log', 'stump'],
  'Panels': ['panel', 'brick'],
  'Walls': ['wall'],
  'Appliances': ['coffee_maker', 'fridge', 'microwave', 'vending'],
  'Electronics': ['computer', 'laptop'],
};

// ============================================
// EDITOR STATE (using EditorState class)
// ============================================

// Create EditorState instance with callbacks for UI updates
const state = new EditorState({
  onPropsChange: () => {
    updatePropCount();
  },
  onSelectionChange: () => {
    updatePropertyPanel();
  },
  onUndoRedoChange: () => {
    updateUndoRedoButtons();
    setUnsavedChanges(true);
  },
  onLocaleChange: () => {
    // Will be wired up later
  },
});

// Keep these as module-level for now (not in EditorState - they're UI/Phaser specific)
let draggedPaletteItem: string | null = null;
let isDragging = false;
let game: Phaser.Game | null = null;
let scene: EditorScene | null = null;

// UI state (not in EditorState)
let isUpdatingPanel = false;

// Position markers (Phaser objects, not data)
let showSpawnPoints = true;
let spawnPointMarkers: Phaser.GameObjects.Container[] = [];

// Unsaved changes tracking
let hasUnsavedChanges = false;

// Props visibility (UI state)
let showProps = true;

// Wall drawing UI state (not data)
let showWalls = true;
let isDrawingWall = false;
let wallDrawMode = false;
let wallStartPoint: { x: number; y: number } | null = null;
let wallPreviewLine: Phaser.GameObjects.Graphics | null = null;
let wallGraphics: Phaser.GameObjects.Graphics | null = null;
const DEFAULT_WALL_COLOR = 0x555555;
const DEFAULT_WALL_THICKNESS = 8;

// Zoom & Pan (camera state)
let currentZoom = 1;
let isPanning = false;
let panStartX = 0;
let panStartY = 0;
let cameraStartX = 0;
let cameraStartY = 0;

// Embedded mode flag
let isEmbeddedMode = false;

// ============================================
// BACKWARDS COMPATIBILITY ALIASES
// These will be removed as migration progresses
// ============================================

// Props array - now references state.props for consistency
// Note: We use state.setProps([]) to clear, state.addProp() to add
const props = state.props;

// Selection - use state but keep aliases
let selectedProp: PropData | null = null;
let selectedPosition: string | null = null;

// Undo/Redo - now uses EditorState methods
// (undoStack, redoStack, isUndoRedoAction are in state)

// Positions dirty flag
let positionsDirty = false;

// Walls array - references state.walls for consistency
const walls = state.walls;
let selectedWall: number | null = null;

// Current locale
let currentLocaleName = '';
let currentLocaleData: LocaleData | null = null;

// ============================================
// HELPER FUNCTIONS
// ============================================

function buildCategories(propList: PropRegistryEntry[], packFilter = 'all'): Record<string, string[]> {
  const categories: Record<string, string[]> = {};
  
  for (const prop of propList) {
    if (packFilter !== 'all' && prop.pack !== packFilter) continue;
    
    const category = prop.category || inferCategory(prop.key);
    
    if (!categories[category]) {
      categories[category] = [];
    }
    categories[category].push(prop.key);
  }
  
  return categories;
}

function inferCategory(key: string): string {
  const lowerKey = key.toLowerCase();
  for (const [category, keywords] of Object.entries(CATEGORY_MAPPINGS)) {
    for (const keyword of keywords) {
      if (lowerKey.includes(keyword)) {
        return category;
      }
    }
  }
  return 'Other';
}

async function loadPropRegistry(): Promise<PropRegistryEntry[]> {
  try {
    const response = await fetch('/sprites/props/props.json');
    const data = await response.json();
    
    PROP_PACKS = data.packs || [];
    PROP_REGISTRY = data.props || [];
    
    // Populate pack filter dropdown
    const packFilter = document.getElementById('packFilter') as HTMLSelectElement;
    if (packFilter) {
      packFilter.innerHTML = '<option value="all">All Packs</option>';
      for (const pack of PROP_PACKS) {
        const opt = document.createElement('option');
        opt.value = pack.id;
        opt.textContent = pack.name;
        packFilter.appendChild(opt);
      }
    }
    
    console.log(`Loaded ${PROP_REGISTRY.length} props from ${PROP_PACKS.length} packs`);
    return PROP_REGISTRY;
  } catch (e) {
    console.error('Failed to load props.json:', e);
    return [];
  }
}

async function loadSpriteRegistry(): Promise<SpriteRegistryEntry[]> {
  try {
    const response = await fetch('/sprites/sprites.json');
    const data = await response.json();
    
    SPRITE_PACKS = data.packs || [];
    SPRITE_REGISTRY = data.sprites || [];
    
    console.log(`Loaded ${SPRITE_REGISTRY.length} sprites from ${SPRITE_PACKS.length} packs`);
    return SPRITE_REGISTRY;
  } catch (e) {
    console.error('Failed to load sprites.json:', e);
    return [];
  }
}

// ============================================
// UNDO/REDO FUNCTIONS (using EditorState)
// ============================================

function saveState(reason = 'unknown'): void {
  if (state.isUndoRedoAction || isUpdatingPanel) return;
  
  console.log(`saveState(${reason}): Saving state with ${props.length} props`);
  state.saveState(reason);
  updateUndoRedoButtons();
  setUnsavedChanges(true);
}

function restoreState(snapshot: StateSnapshot): void {
  if (!scene) return;
  
  state.isUndoRedoAction = true;
  
  // Destroy all existing prop sprites
  for (const prop of props) {
    prop.sprite.destroy();
  }
  state.clearProps();
  scene.propSprites.clear();
  selectedProp = null;
  
  // Recreate props from snapshot
  for (const propData of snapshot.props) {
    scene.addProp(propData.key, propData.x, propData.y, propData.scale, propData.depth, propData.flipX, propData.angle || 0, true);
  }
  
  state.isUndoRedoAction = false;
  updatePropertyPanel();
  updatePropCount();
}

function undo(): void {
  const snapshot = state.popUndo();
  if (!snapshot) return;
  
  restoreState(snapshot);
  updateUndoRedoButtons();
  console.log('Undo - canUndo:', state.canUndo(), 'canRedo:', state.canRedo());
}

function redo(): void {
  const snapshot = state.popRedo();
  if (!snapshot) return;
  
  restoreState(snapshot);
  updateUndoRedoButtons();
  console.log('Redo - canUndo:', state.canUndo(), 'canRedo:', state.canRedo());
}

function updateUndoRedoButtons(): void {
  const undoBtn = document.getElementById('undoBtn') as HTMLButtonElement | null;
  const redoBtn = document.getElementById('redoBtn') as HTMLButtonElement | null;
  if (undoBtn) undoBtn.disabled = !state.canUndo();
  if (redoBtn) redoBtn.disabled = !state.canRedo();
}

function setUnsavedChanges(unsaved: boolean): void {
  hasUnsavedChanges = unsaved;
  const saveBtn = document.getElementById('saveBtn');
  const localeSelect = document.getElementById('localeSelect') as HTMLSelectElement | null;
  
  if (saveBtn) {
    if (unsaved) {
      saveBtn.textContent = '💾 Save *';
      saveBtn.style.borderColor = '#f39c12';
    } else {
      saveBtn.textContent = '💾 Save';
      saveBtn.style.borderColor = '';
    }
  }
  
  // Update locale dropdown to show unsaved indicator
  if (localeSelect && currentLocaleName) {
    const option = localeSelect.querySelector(`option[value="${currentLocaleName}"]`) as HTMLOptionElement | null;
    if (option) {
      const baseName = option.textContent?.replace(' *', '') || currentLocaleName;
      option.textContent = unsaved ? `${baseName} *` : baseName;
    }
  }
  
  // In embedded mode, notify parent of dirty state changes
  if (isEmbeddedMode) {
    window.parent.postMessage({
      type: 'editor:dirty',
      isDirty: unsaved,
      localeName: currentLocaleName || 'untitled',
    }, '*');
  }
}

// Helper to convert integer color to hex string
function intToHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

// Helper to convert hex string to integer color
function hexToInt(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}

function updateLocaleSettingsPanel(localeData: import('./types').LocaleData | null): void {
  const localeSettings = document.getElementById('localeSettings');
  const wallColorInput = document.getElementById('localeWallColor') as HTMLInputElement | null;
  const floorColorInput = document.getElementById('localeFloorColor') as HTMLInputElement | null;
  const bgColorInput = document.getElementById('localeBgColor') as HTMLInputElement | null;
  
  if (!localeSettings) return;
  
  if (localeData?.locale) {
    // Show panel and populate colors
    localeSettings.style.display = 'block';
    
    const wallColor = localeData.locale.wallColor ?? 0x8b7355;
    const floorColor = localeData.locale.floorColor ?? 0xdeb887;
    const bgColor = localeData.locale.background ?? 0x654321;
    
    if (wallColorInput) wallColorInput.value = intToHex(wallColor);
    if (floorColorInput) floorColorInput.value = intToHex(floorColor);
    if (bgColorInput) bgColorInput.value = intToHex(bgColor);
  } else {
    // Hide panel
    localeSettings.style.display = 'none';
  }
}

// ============================================
// UI FUNCTIONS
// ============================================

function populatePropPalette(packFilter = 'all'): void {
  const container = document.getElementById('propCategories');
  if (!container) return;
  
  container.innerHTML = '';
  
  const categories = buildCategories(PROP_REGISTRY, packFilter);
  const sortedCategories = Object.entries(categories).sort((a, b) => a[0].localeCompare(b[0]));
  
  for (const [category, propKeys] of sortedCategories) {
    if (propKeys.length === 0) continue;
    
    const categoryDiv = document.createElement('div');
    categoryDiv.className = 'category';
    categoryDiv.innerHTML = `<div class="category-title">${category} <span style="opacity:0.5">(${propKeys.length})</span></div>`;
    
    const grid = document.createElement('div');
    grid.className = 'prop-grid';
    
    for (const key of propKeys) {
      const propData = PROP_REGISTRY.find(p => p.key === key);
      if (!propData) continue;
      
      const item = document.createElement('div');
      item.className = 'prop-item';
      item.title = propData.description || key;
      item.dataset.key = key;
      item.innerHTML = `<img src="${propData.path}" alt="${key}">`;
      
      // Double-click to insert at center
      item.addEventListener('dblclick', (e) => {
        e.preventDefault();
        if (scene) {
          const cam = scene.cameras.main;
          const centerX = cam.scrollX + cam.width / 2;
          const centerY = cam.scrollY + cam.height / 2;
          const newProp = scene.addProp(key, centerX, centerY, 2, null, false, 0);
          if (newProp) {
            selectedProp = newProp;
            updatePropertyPanel();
          }
        }
      });
      
      // Drag to place
      item.addEventListener('mousedown', (e) => {
        if ((e as MouseEvent).detail >= 2) return;
        draggedPaletteItem = key;
        
        const preview = document.createElement('img');
        preview.src = propData.path;
        preview.className = 'drag-preview';
        preview.style.left = (e as MouseEvent).clientX + 'px';
        preview.style.top = (e as MouseEvent).clientY + 'px';
        preview.style.transform = 'translate(-50%, -50%) scale(2)';
        preview.id = 'dragPreview';
        document.body.appendChild(preview);
        
        const movePreview = (ev: MouseEvent) => {
          preview.style.left = ev.clientX + 'px';
          preview.style.top = ev.clientY + 'px';
        };
        
        const removePreview = () => {
          preview.remove();
          document.removeEventListener('mousemove', movePreview);
          document.removeEventListener('mouseup', removePreview);
        };
        
        document.addEventListener('mousemove', movePreview);
        document.addEventListener('mouseup', removePreview);
      });
      
      grid.appendChild(item);
    }
    
    categoryDiv.appendChild(grid);
    container.appendChild(categoryDiv);
  }
}

// Currently selected sprite for preview
let selectedSprite: SpriteRegistryEntry | null = null;
let spritePreviewAnimation: number | null = null;

function populateSpriteBrowser(filter = ''): void {
  const container = document.getElementById('spriteBrowser');
  if (!container) return;
  
  container.innerHTML = '';
  
  const filterLower = filter.toLowerCase();
  const filteredSprites = SPRITE_REGISTRY.filter(sprite => {
    if (!filter) return true;
    const matchesKey = sprite.key.toLowerCase().includes(filterLower);
    const matchesName = sprite.name?.toLowerCase().includes(filterLower);
    const matchesTags = sprite.tags?.some(tag => tag.toLowerCase().includes(filterLower));
    const matchesDesc = sprite.description?.toLowerCase().includes(filterLower);
    return matchesKey || matchesName || matchesTags || matchesDesc;
  });
  
  // Sort alphabetically by name
  const sortedSprites = filteredSprites.sort((a, b) => 
    (a.name || a.key).localeCompare(b.name || b.key)
  );
  
  for (const sprite of sortedSprites) {
    const item = document.createElement('div');
    item.className = 'sprite-item';
    item.dataset.key = sprite.key;
    item.title = sprite.description || sprite.name || sprite.key;
    
    // Create a mini canvas to show the first frame
    const canvas = document.createElement('canvas');
    canvas.width = 48;
    canvas.height = 48;
    canvas.style.imageRendering = 'pixelated';
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = false;
        // Draw the first frame (idle frame, typically column 1 of row 0)
        const frameX = sprite.frameWidth; // Second column (idle pose)
        const frameY = 0;
        const scale = Math.min(48 / sprite.frameWidth, 48 / sprite.frameHeight);
        const drawWidth = sprite.frameWidth * scale;
        const drawHeight = sprite.frameHeight * scale;
        const offsetX = (48 - drawWidth) / 2;
        const offsetY = (48 - drawHeight) / 2;
        ctx.drawImage(
          img,
          frameX, frameY, sprite.frameWidth, sprite.frameHeight,
          offsetX, offsetY, drawWidth, drawHeight
        );
      }
    };
    img.src = sprite.path;
    
    const label = document.createElement('div');
    label.className = 'sprite-item-label';
    label.textContent = sprite.name || sprite.key;
    
    item.appendChild(canvas);
    item.appendChild(label);
    
    // Click to show preview
    item.addEventListener('click', () => {
      showSpritePreview(sprite);
    });
    
    container.appendChild(item);
  }
}

function showSpritePreview(sprite: SpriteRegistryEntry): void {
  selectedSprite = sprite;
  
  // Show the preview panel
  setDisplay('noSelection', 'none');
  setDisplay('propProperties', 'none');
  setDisplay('positionProperties', 'none');
  setDisplay('spritePreviewPanel', 'block');
  
  setText('spritePreviewName', sprite.name || sprite.key);
  setText('spritePreviewInfo', `${sprite.frameWidth}×${sprite.frameHeight} • ${sprite.key}`);
  setText('selectedStatus', sprite.key);
  
  // Start the animation preview
  startSpriteAnimation(sprite, 'down');
  
  // Set up direction buttons
  document.querySelectorAll('.sprite-direction-btn').forEach(btn => {
    btn.classList.remove('active');
    if ((btn as HTMLElement).dataset.dir === 'down') {
      btn.classList.add('active');
    }
  });
}

function startSpriteAnimation(sprite: SpriteRegistryEntry, direction: string): void {
  // Stop any existing animation
  if (spritePreviewAnimation) {
    cancelAnimationFrame(spritePreviewAnimation);
  }
  
  const canvas = document.getElementById('spritePreviewCanvas') as HTMLCanvasElement;
  if (!canvas) return;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    ctx.imageSmoothingEnabled = false;
    
    // Determine which row based on direction
    let row = 0;
    switch (direction) {
      case 'down': row = 0; break;
      case 'left': row = 1; break;
      case 'right': row = 2; break;
      case 'up': row = 3; break;
    }
    
    const columns = sprite.columns || 3;
    let frame = 0;
    let lastTime = 0;
    const frameRate = 8;
    const frameDelay = 1000 / frameRate;
    
    const animate = (time: number) => {
      if (time - lastTime > frameDelay) {
        frame = (frame + 1) % columns;
        lastTime = time;
        
        // Clear and draw
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        const frameX = frame * sprite.frameWidth;
        const frameY = row * sprite.frameHeight;
        const scale = Math.min(canvas.width / sprite.frameWidth, canvas.height / sprite.frameHeight);
        const drawWidth = sprite.frameWidth * scale;
        const drawHeight = sprite.frameHeight * scale;
        const offsetX = (canvas.width - drawWidth) / 2;
        const offsetY = (canvas.height - drawHeight) / 2;
        
        ctx.drawImage(
          img,
          frameX, frameY, sprite.frameWidth, sprite.frameHeight,
          offsetX, offsetY, drawWidth, drawHeight
        );
      }
      
      spritePreviewAnimation = requestAnimationFrame(animate);
    };
    
    spritePreviewAnimation = requestAnimationFrame(animate);
  };
  img.src = sprite.path;
}

function updatePropertyPanel(): void {
  isUpdatingPanel = true;
  
  if (selectedProp) {
    setDisplay('noSelection', 'none');
    setDisplay('propProperties', 'block');
    setDisplay('positionProperties', 'none');
    setDisplay('spritePreviewPanel', 'none');
    setText('selectedStatus', selectedProp.key);
    
    setValue('propKey', selectedProp.key);
    setValue('propX', Math.round(selectedProp.x));
    setValue('propY', Math.round(selectedProp.y));
    setValue('propScale', selectedProp.scale);
    setText('scaleValue', selectedProp.scale.toFixed(1));
    setValue('propDepth', Math.round(selectedProp.depth));
    setChecked('propFlipX', selectedProp.flipX);
    setText('angleValue', (selectedProp.angle || 0) + '°');
  } else if (selectedPosition) {
    setDisplay('noSelection', 'none');
    setDisplay('propProperties', 'none');
  } else {
    setDisplay('noSelection', 'block');
    setDisplay('propProperties', 'none');
    setDisplay('positionProperties', 'none');
    setText('selectedStatus', 'None');
  }
  
  isUpdatingPanel = false;
}

function showPositionProperties(name: string, pos: Position): void {
  selectedProp = null;
  if (scene) scene.clearSelection();
  
  setDisplay('noSelection', 'none');
  setDisplay('propProperties', 'none');
  setDisplay('positionProperties', 'block');
  
  setValue('posName', name);
  setValue('posX', Math.round(pos.x));
  setValue('posY', Math.round(pos.y));
  setValue('posLabel', pos.label || '');
  
  setText('selectedStatus', `📍 ${name}`);
}

function hidePositionProperties(): void {
  setDisplay('positionProperties', 'none');
  setDisplay('noPositionSelection', 'block');
  setText('selectedStatus', 'None');
}

function updatePropCount(): void {
  setText('propCount', props.length);
}

function updateZoomDisplay(): void {
  const percentage = Math.round(currentZoom * 100);
  const el = document.getElementById('zoomLevel');
  if (el) el.textContent = `${percentage}%`;
}

function resetZoom(): void {
  if (!scene) return;
  
  const worldWidth = scene.worldWidth || 800;
  const worldHeight = scene.worldHeight || 450;
  const scaleX = 800 / worldWidth;
  const scaleY = 450 / worldHeight;
  currentZoom = Math.min(scaleX, scaleY, 1);
  
  const camera = scene.cameras.main;
  camera.setZoom(currentZoom);
  camera.centerOn(worldWidth / 2, worldHeight / 2);
  
  updateZoomDisplay();
}

// ============================================
// YAML GENERATION
// ============================================

function generateFullYaml(): string {
  const localeData = currentLocaleData?.locale ? { ...currentLocaleData.locale } : {
    name: currentLocaleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    world: { width: scene?.worldWidth || 800, height: scene?.worldHeight || 450 },
    wallColor: 0x8b7355,
    floorColor: 0xdeb887,
    background: 0x654321
  };
  
  if (scene?.currentLocalePositions) {
    const positions: Record<string, { x: number; y: number; label?: string }> = {};
    for (const [name, pos] of Object.entries(scene.currentLocalePositions)) {
      positions[name] = {
        x: Math.round(pos.x),
        y: Math.round(pos.y),
      };
      if (pos.label) {
        positions[name].label = pos.label;
      }
    }
    localeData.positions = positions;
  }
  
  localeData.props = props.map(p => {
    const prop: { key: string; x: number; y: number; scale: number; depth?: number; flipX?: boolean; angle?: number } = {
      key: p.key,
      x: Math.round(p.x),
      y: Math.round(p.y),
      scale: parseFloat(p.scale.toFixed(1)),
    };
    if (Math.round(p.depth) !== Math.round(p.y)) {
      prop.depth = Math.round(p.depth);
    }
    if (p.flipX) {
      prop.flipX = true;
    }
    if (p.angle) {
      prop.angle = p.angle;
    }
    return prop;
  });
  
  if (walls.length > 0) {
    localeData.walls = walls.map(w => ({
      x1: Math.round(w.x1),
      y1: Math.round(w.y1),
      x2: Math.round(w.x2),
      y2: Math.round(w.y2),
      ...(w.color !== DEFAULT_WALL_COLOR ? { color: w.color } : {}),
      ...(w.thickness !== DEFAULT_WALL_THICKNESS ? { thickness: w.thickness } : {})
    }));
  }
  
  const fullData = { locale: localeData };
  return jsyaml.dump(fullData, {
    indent: 2,
    lineWidth: -1,
    quotingType: "'",
    forceQuotes: false,
  });
}

// ============================================
// SAVE/LOAD TO BROWSER STORAGE
// ============================================

function getStorageKey(localeName: string): string {
  // Use "draft" prefix to indicate this is an unsaved local draft, not the canonical locale
  return `pixelgame_editor_draft_${localeName}`;
}

function saveToStorage(): void {
  const propsArray = props.map(p => ({
    key: p.key,
    x: Math.round(p.x),
    y: Math.round(p.y),
    scale: parseFloat(p.scale.toFixed(1)),
    ...(Math.round(p.depth) !== Math.round(p.y) ? { depth: Math.round(p.depth) } : {}),
    ...(p.flipX ? { flipX: true } : {}),
    ...(p.angle ? { angle: p.angle } : {}),
  }));
  
  const key = getStorageKey(currentLocaleName);
  localStorage.setItem(key, JSON.stringify(propsArray));
  console.log(`Saved ${propsArray.length} props to ${key}`);
  
  const saveBtn = document.getElementById('saveBtn');
  if (saveBtn) {
    const originalText = saveBtn.textContent;
    saveBtn.textContent = '✓ Saved!';
    saveBtn.style.background = '#27ae60';
    setTimeout(() => {
      saveBtn.textContent = originalText || '💾 Save';
      saveBtn.style.background = '';
    }, 1500);
  }
}

function clearStorage(localeName: string): void {
  const key = getStorageKey(localeName);
  localStorage.removeItem(key);
  console.log(`Cleared saved props for ${localeName}`);
}

function hasSavedLayout(localeName: string): boolean {
  return localStorage.getItem(getStorageKey(localeName)) !== null;
}

function updateSaveButtonState(localeName: string): void {
  const resetBtn = document.getElementById('resetBtn');
  if (resetBtn) {
    resetBtn.style.display = hasSavedLayout(localeName) ? 'inline-block' : 'none';
  }
}

// ============================================
// SAVE TO BACKEND
// ============================================

async function saveToBackend(): Promise<void> {
  // In embedded mode, send to parent instead of backend
  if (isEmbeddedMode) {
    const yaml = generateFullYaml();
    window.parent.postMessage({
      type: 'editor:save',
      yaml,
      localeName: currentLocaleName
    }, '*');
    setUnsavedChanges(false);
    return;
  }
  
  const saveBtn = document.getElementById('saveBtn');
  const originalText = saveBtn?.textContent || '💾 Save';
  
  if (!currentLocaleName) {
    alert('Please create or load a locale first.');
    return;
  }
  
  // In demo mode, just save to localStorage (no backend available)
  if (isDemoMode()) {
    saveToStorage();
    setUnsavedChanges(false);
    
    if (saveBtn) {
      saveBtn.textContent = '✓ Saved locally!';
      saveBtn.style.background = '#6366f1';
      
      setTimeout(() => {
        saveBtn.textContent = '💾 Save';
        saveBtn.style.background = '';
        saveBtn.style.borderColor = '';
      }, 2000);
    }
    return;
  }
  
  try {
    if (saveBtn) {
      saveBtn.textContent = '⏳ Saving...';
      (saveBtn as HTMLButtonElement).disabled = true;
    }
    
    const newYaml = generateFullYaml();
    positionsDirty = false;
    
    const saveResponse = await fetch(`/api/locales/${currentLocaleName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/yaml' },
      body: newYaml,
    });
    
    if (!saveResponse.ok) {
      const errorData = await saveResponse.json();
      throw new Error(errorData.error || 'Failed to save');
    }
    
    const result = await saveResponse.json();
    console.log('Saved to backend:', result);
    
    saveToStorage();
    setUnsavedChanges(false);
    
    if (saveBtn) {
      saveBtn.textContent = '✓ Saved!';
      saveBtn.style.background = '#27ae60';
      
      setTimeout(() => {
        saveBtn.textContent = '💾 Save';
        saveBtn.style.background = '';
        saveBtn.style.borderColor = '';
        (saveBtn as HTMLButtonElement).disabled = false;
      }, 2000);
    }
    
  } catch (error) {
    console.error('Failed to save to backend:', error);
    
    if (saveBtn) {
      saveBtn.textContent = '✗ Error!';
      saveBtn.style.background = '#c0392b';
    }
    
    alert(`Failed to save: ${(error as Error).message}\n\nYour changes are still saved locally in the browser.`);
    saveToStorage();
    
    if (saveBtn) {
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.style.background = '';
        (saveBtn as HTMLButtonElement).disabled = false;
      }, 2000);
    }
  }
}


// ============================================
// EDITOR CONTEXT
// ============================================

// Create the EditorContext that bridges editor-main.ts state with EditorScene
function createEditorContext(): EditorContext {
  return {
    // State object
    state,
    
    // Prop registry
    getPropRegistry: () => PROP_REGISTRY,
    
    // Selection state
    getSelectedProp: () => selectedProp,
    setSelectedProp: (prop) => { selectedProp = prop; },
    getSelectedPosition: () => selectedPosition,
    setSelectedPosition: (name) => { selectedPosition = name; },
    getSelectedWall: () => selectedWall,
    setSelectedWall: (index) => { selectedWall = index; },
    
    // Visibility flags
    getShowProps: () => showProps,
    setShowProps: (show) => { showProps = show; },
    getShowWalls: () => showWalls,
    setShowWalls: (show) => { showWalls = show; },
    getShowSpawnPoints: () => showSpawnPoints,
    setShowSpawnPoints: (show) => { showSpawnPoints = show; },
    
    // Zoom state
    getCurrentZoom: () => currentZoom,
    setCurrentZoom: (zoom) => { currentZoom = zoom; },
    
    // Interaction state
    getIsDragging: () => isDragging,
    setIsDragging: (dragging) => { isDragging = dragging; },
    getIsPanning: () => isPanning,
    setIsPanning: (panning) => { isPanning = panning; },
    getPanStart: () => ({ x: panStartX, y: panStartY }),
    setPanStart: (x, y) => { panStartX = x; panStartY = y; },
    getCameraStart: () => ({ x: cameraStartX, y: cameraStartY }),
    setCameraStart: (x, y) => { cameraStartX = x; cameraStartY = y; },
    
    // Wall drawing state
    getWallDrawMode: () => wallDrawMode,
    setWallDrawMode: (mode) => { wallDrawMode = mode; },
    getIsDrawingWall: () => isDrawingWall,
    setIsDrawingWall: (drawing) => { isDrawingWall = drawing; },
    getWallStartPoint: () => wallStartPoint,
    setWallStartPoint: (point) => { wallStartPoint = point; },
    getWallPreviewLine: () => wallPreviewLine,
    setWallPreviewLine: (line) => { wallPreviewLine = line; },
    getWallGraphics: () => wallGraphics,
    setWallGraphics: (graphics) => { wallGraphics = graphics; },
    
    // Locale state
    getCurrentLocaleName: () => currentLocaleName,
    setCurrentLocaleName: (name) => { currentLocaleName = name; },
    getCurrentLocaleData: () => currentLocaleData,
    setCurrentLocaleData: (data) => { currentLocaleData = data; },
    
    // Position markers
    getSpawnPointMarkers: () => spawnPointMarkers,
    setSpawnPointMarkers: (markers) => { spawnPointMarkers = markers; },
    
    // Callbacks
    onSaveState: saveState,
    onUpdatePropertyPanel: updatePropertyPanel,
    onUpdatePropCount: updatePropCount,
    onUpdateZoomDisplay: updateZoomDisplay,
    onUpdateSaveButtonState: updateSaveButtonState,
    onResetZoom: resetZoom,
    onUpdateUndoRedoButtons: updateUndoRedoButtons,
    onSetUnsavedChanges: setUnsavedChanges,
    onShowPositionProperties: showPositionProperties,
    onHidePositionProperties: hidePositionProperties,
    onSetPositionsDirty: (dirty) => { positionsDirty = dirty; },
    onClearUndoRedo: () => { state.clearHistory(); },
    onGetIsUndoRedoAction: () => state.isUndoRedoAction,
    onLocaleLoaded: (localeData) => { updateLocaleSettingsPanel(localeData); },
    
    // Scene reference setter
    onSceneReady: (s) => { scene = s; },
  };
}


// ============================================
// INITIALIZE
// ============================================

export async function initEditor(): Promise<void> {
  await loadPropRegistry();
  
  // Set up the context bridge between editor-main.ts and EditorScene
  EditorScene.setContext(createEditorContext());
  
  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    width: 800,
    height: 450,
    parent: 'game-container',
    backgroundColor: '#1a1a2e',
    pixelArt: true,
    antialias: false,
    roundPixels: true,
    render: {
      antialias: false,
      pixelArt: true,
      roundPixels: true,
    },
    scene: EditorScene,
  };
  
  game = new Phaser.Game(config);
  
  populatePropPalette();
  
  await loadSpriteRegistry();
  populateSpriteBrowser();
  
  setupEventHandlers();
  setupEmbeddedMode();
}

function setupEventHandlers(): void {
  // Tab switching
  document.querySelectorAll('.palette-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.palette-tab').forEach(t => t.classList.remove('active'));
      (tab as HTMLElement).classList.add('active');
      
      document.querySelectorAll('.palette-content').forEach(c => c.classList.remove('active'));
      const tabId = (tab as HTMLElement).dataset.tab;
      const tabContent = document.getElementById(tabId + 'Tab');
      if (tabContent) tabContent.classList.add('active');
    });
  });
  
  // Pack filter
  const packFilter = document.getElementById('packFilter');
  if (packFilter) {
    packFilter.addEventListener('change', (e) => {
      currentPackFilter = (e.target as HTMLSelectElement).value;
      populatePropPalette(currentPackFilter);
    });
  }
  
  // Search filter for props
  const propSearch = document.getElementById('propSearch');
  if (propSearch) {
    propSearch.addEventListener('input', (e) => {
      const search = (e.target as HTMLInputElement).value.toLowerCase();
      document.querySelectorAll('.prop-item').forEach(item => {
        const key = (item as HTMLElement).dataset.key?.toLowerCase() || '';
        const propData = PROP_REGISTRY.find(p => p.key === (item as HTMLElement).dataset.key);
        const desc = propData?.description?.toLowerCase() || '';
        const matches = key.includes(search) || desc.includes(search);
        (item as HTMLElement).style.display = matches ? '' : 'none';
      });
    });
  }
  
  // Search filter for sprites
  const spriteSearch = document.getElementById('spriteSearch');
  if (spriteSearch) {
    spriteSearch.addEventListener('input', (e) => {
      const search = (e.target as HTMLInputElement).value;
      populateSpriteBrowser(search);
    });
  }
  
  // Sprite preview direction buttons
  document.querySelectorAll('.sprite-direction-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (!selectedSprite) return;
      
      // Update active state
      document.querySelectorAll('.sprite-direction-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      
      const direction = (btn as HTMLElement).dataset.dir || 'down';
      startSpriteAnimation(selectedSprite, direction);
    });
  });
  
  // Sprite preview click to copy key
  const spritePreviewPanel = document.getElementById('spritePreviewPanel');
  if (spritePreviewPanel) {
    spritePreviewPanel.addEventListener('click', (e) => {
      if (selectedSprite && (e.target as HTMLElement).closest('#spritePreviewCanvas')) {
        navigator.clipboard.writeText(selectedSprite.key).then(() => {
          alert(`Copied sprite key: ${selectedSprite!.key}`);
        });
      }
    });
  }
  
  // Load locale
  const loadLocaleBtn = document.getElementById('loadLocaleBtn');
  if (loadLocaleBtn) {
    loadLocaleBtn.addEventListener('click', async () => {
      const select = document.getElementById('localeSelect') as HTMLSelectElement;
      if (scene && select) {
        await scene.loadLocaleProps(select.value);
      }
    });
  }
  
  // Save button
  const saveBtn = document.getElementById('saveBtn');
  if (saveBtn) {
    saveBtn.addEventListener('click', () => saveToBackend());
  }
  
  // Reset button
  const resetBtn = document.getElementById('resetBtn');
  if (resetBtn) {
    resetBtn.addEventListener('click', async () => {
      if (scene && confirm(`Reload ${currentLocaleName} from file? This will discard unsaved changes.`)) {
        clearStorage(currentLocaleName);
        await scene.loadLocaleProps(currentLocaleName);
      }
    });
  }
  
  // Toggle spawn points
  const toggleSpawnsBtn = document.getElementById('toggleSpawnsBtn');
  if (toggleSpawnsBtn) {
    toggleSpawnsBtn.addEventListener('click', () => {
      if (!scene) return;
      const visible = scene.toggleSpawnPoints();
      toggleSpawnsBtn.style.background = visible ? '#27ae60' : '';
    });
  }
  
  // Add position
  const addPositionBtn = document.getElementById('addPositionBtn');
  if (addPositionBtn) {
    addPositionBtn.addEventListener('click', () => {
      const name = prompt('Enter position name (e.g., "by_window", "desk_back_left"):');
      if (name && name.trim() && scene) {
        const cleanName = name.trim().toLowerCase().replace(/\s+/g, '_');
        scene.addPosition(cleanName);
      }
    });
  }
  
  // Undo/Redo
  const undoBtn = document.getElementById('undoBtn');
  if (undoBtn) undoBtn.addEventListener('click', () => undo());
  
  const redoBtn = document.getElementById('redoBtn');
  if (redoBtn) redoBtn.addEventListener('click', () => redo());
  
  // Keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      saveToBackend();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      redo();
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if ((selectedProp || selectedPosition || selectedWall !== null) && 
          (document.activeElement as HTMLElement)?.tagName !== 'INPUT') {
        e.preventDefault();
        scene?.deleteSelected();
      }
    }
    if (e.key === 'Escape') {
      if (wallDrawMode && scene) {
        scene.setWallDrawMode(false);
        wallDrawMode = false;
      }
    }
  });
  
  // ... more event handlers would be added here
  // This is a subset showing the pattern
  
  // Drag from palette to canvas
  document.addEventListener('mouseup', (e) => {
    if (draggedPaletteItem && scene) {
      const canvas = document.querySelector('#game-container canvas');
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        if (x >= 0 && x <= 800 && y >= 0 && y <= 450) {
          const worldPoint = scene.screenToWorld(x, y);
          const newProp = scene.addProp(draggedPaletteItem, Math.round(worldPoint.x), Math.round(worldPoint.y), 2);
          if (newProp) {
            selectedProp = newProp;
            updatePropertyPanel();
          }
        }
      }
    }
    draggedPaletteItem = null;
  });
  
  // Export YAML modal
  const exportYamlBtn = document.getElementById('exportYamlBtn');
  if (exportYamlBtn) {
    exportYamlBtn.addEventListener('click', () => {
      const yaml = generateFullYaml();
      setValue('exportYamlCode', yaml);
      setText('yamlFilename', `${currentLocaleName}.yaml`);
      const modal = document.getElementById('exportYamlModal');
      if (modal) modal.classList.add('active');
    });
  }
  
  // Close YAML modal buttons
  const closeYamlModal = document.getElementById('closeYamlModal');
  if (closeYamlModal) {
    closeYamlModal.addEventListener('click', () => {
      const modal = document.getElementById('exportYamlModal');
      if (modal) modal.classList.remove('active');
    });
  }
  
  const closeYamlModalBtn = document.getElementById('closeYamlModalBtn');
  if (closeYamlModalBtn) {
    closeYamlModalBtn.addEventListener('click', () => {
      const modal = document.getElementById('exportYamlModal');
      if (modal) modal.classList.remove('active');
    });
  }
  
  const copyYamlBtn = document.getElementById('copyYamlBtn');
  if (copyYamlBtn) {
    copyYamlBtn.addEventListener('click', () => {
      const code = document.getElementById('exportYamlCode') as HTMLTextAreaElement;
      if (code) {
        code.select();
        document.execCommand('copy');
        alert('Copied to clipboard!');
      }
    });
  }
  
  const downloadYamlBtn = document.getElementById('downloadYamlBtn');
  if (downloadYamlBtn) {
    downloadYamlBtn.addEventListener('click', () => {
      const code = document.getElementById('exportYamlCode') as HTMLTextAreaElement;
      if (code) {
        const yaml = code.value;
        const blob = new Blob([yaml], { type: 'text/yaml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${currentLocaleName}.yaml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    });
  }
  
  // NEW LOCALE MODAL
  const newLocaleBtn = document.getElementById('newLocaleBtn');
  if (newLocaleBtn) {
    newLocaleBtn.addEventListener('click', () => {
      setValue('newLocaleName', '');
      setValue('newLocaleWidth', '800');
      setValue('newLocaleHeight', '450');
      setValue('newLocaleWallColor', '#8b7355');
      setValue('newLocaleFloorColor', '#deb887');
      setValue('newLocaleBgColor', '#654321');
      const modal = document.getElementById('newLocaleModal');
      if (modal) modal.classList.add('active');
      const nameInput = document.getElementById('newLocaleName');
      if (nameInput) nameInput.focus();
    });
  }
  
  const closeNewLocale = document.getElementById('closeNewLocale');
  if (closeNewLocale) {
    closeNewLocale.addEventListener('click', () => {
      const modal = document.getElementById('newLocaleModal');
      if (modal) modal.classList.remove('active');
    });
  }
  
  const cancelNewLocaleBtn = document.getElementById('cancelNewLocaleBtn');
  if (cancelNewLocaleBtn) {
    cancelNewLocaleBtn.addEventListener('click', () => {
      const modal = document.getElementById('newLocaleModal');
      if (modal) modal.classList.remove('active');
    });
  }
  
  const createLocaleBtn = document.getElementById('createLocaleBtn');
  if (createLocaleBtn) {
    createLocaleBtn.addEventListener('click', () => {
      const nameInput = document.getElementById('newLocaleName') as HTMLInputElement;
      const name = nameInput?.value.trim() || '';
      
      if (!name || !/^[a-z][a-z0-9_]*$/.test(name)) {
        alert('Please enter a valid locale name (lowercase letters, numbers, underscores only, must start with letter)');
        return;
      }
      
      const widthInput = document.getElementById('newLocaleWidth') as HTMLInputElement;
      const heightInput = document.getElementById('newLocaleHeight') as HTMLInputElement;
      const wallColorInput = document.getElementById('newLocaleWallColor') as HTMLInputElement;
      const floorColorInput = document.getElementById('newLocaleFloorColor') as HTMLInputElement;
      const bgColorInput = document.getElementById('newLocaleBgColor') as HTMLInputElement;
      
      const width = parseInt(widthInput?.value) || 800;
      const height = parseInt(heightInput?.value) || 450;
      const wallColor = wallColorInput?.value || '#8b7355';
      const floorColor = floorColorInput?.value || '#deb887';
      const bgColor = bgColorInput?.value || '#654321';
      
      const hexToInt = (hex: string) => parseInt(hex.replace('#', ''), 16);
      
      const newLocaleData: LocaleData = {
        locale: {
          name: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          world: { width, height },
          wallColor: hexToInt(wallColor),
          floorColor: hexToInt(floorColor),
          background: hexToInt(bgColor),
          positions: {
            center: { x: Math.round(width / 2), y: Math.round(height / 2) },
            entrance: { x: Math.round(width / 2), y: height - 50 }
          },
          props: []
        }
      };
      
      currentLocaleName = name;
      currentLocaleData = newLocaleData;
      state.clearProps();
      walls.length = 0; // Clear in place
      if (scene) {
        scene.currentLocalePositions = { ...newLocaleData.locale.positions! };
        scene.drawBackground(newLocaleData);
        scene.clearAllProps();
        scene.renderSpawnPoints(name);
      }
      
      // Add to dropdown
      const select = document.getElementById('localeSelect') as HTMLSelectElement;
      if (select) {
        const existingOption = Array.from(select.options).find(o => o.value === name);
        if (!existingOption) {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = newLocaleData.locale.name + ' (new)';
          select.appendChild(option);
        }
        select.value = name;
      }
      
      saveToStorage();
      
      // Reset undo/redo and mark as unsaved (new locale needs to be saved to backend)
      state.clearHistory();
      updateUndoRedoButtons();
      setUnsavedChanges(true);
      
      // Update locale settings panel with new colors
      updateLocaleSettingsPanel(newLocaleData);
      
      const modal = document.getElementById('newLocaleModal');
      if (modal) modal.classList.remove('active');
      console.log(`Created new locale: ${name}`);
    });
  }
  
  // Toggle props visibility
  const togglePropsBtn = document.getElementById('togglePropsBtn');
  if (togglePropsBtn) {
    togglePropsBtn.addEventListener('click', () => {
      if (!scene) return;
      const visible = scene.toggleProps();
      togglePropsBtn.style.background = visible ? '#27ae60' : '';
    });
  }
  
  // Wall controls
  const toggleWallsBtn = document.getElementById('toggleWallsBtn');
  if (toggleWallsBtn) {
    toggleWallsBtn.addEventListener('click', () => {
      if (!scene) return;
      const visible = scene.toggleWalls();
      toggleWallsBtn.style.background = visible ? '#27ae60' : '';
      const drawBtn = document.getElementById('drawWallBtn');
      if (drawBtn) {
        drawBtn.style.display = visible ? 'inline-block' : 'none';
        if (!visible) {
          scene.setWallDrawMode(false);
          drawBtn.style.background = '';
        }
      }
    });
  }
  
  const drawWallBtn = document.getElementById('drawWallBtn');
  if (drawWallBtn) {
    drawWallBtn.addEventListener('click', () => {
      if (!scene) return;
      wallDrawMode = !wallDrawMode;
      scene.setWallDrawMode(wallDrawMode);
      drawWallBtn.style.background = wallDrawMode ? '#e94560' : '';
      drawWallBtn.textContent = wallDrawMode ? '✏️ Drawing...' : '✏️ Draw';
    });
  }
  
  // Clear props
  const clearPropsBtn = document.getElementById('clearPropsBtn');
  if (clearPropsBtn) {
    clearPropsBtn.addEventListener('click', () => {
      if (scene && confirm('Clear all props?')) {
        saveState('clearAll');
        scene.clearAllProps();
      }
    });
  }
  
  // Delete selected
  const deleteSelectedBtn = document.getElementById('deleteSelectedBtn');
  if (deleteSelectedBtn) {
    deleteSelectedBtn.addEventListener('click', () => {
      scene?.deleteSelected();
    });
  }
  
  // Property inputs
  setupPropertyInputs();
  
  // Zoom reset
  const resetZoomBtn = document.getElementById('resetZoomBtn');
  if (resetZoomBtn) {
    resetZoomBtn.addEventListener('click', resetZoom);
  }
  
  // Initialize button states
  const initSpawnsBtn = document.getElementById('toggleSpawnsBtn');
  if (initSpawnsBtn) initSpawnsBtn.style.background = '#27ae60';
  
  const initPropsBtn = document.getElementById('togglePropsBtn');
  if (initPropsBtn) initPropsBtn.style.background = '#27ae60';
  
  const initWallsBtn = document.getElementById('toggleWallsBtn');
  if (initWallsBtn) initWallsBtn.style.background = '#27ae60';
}

function setupPropertyInputs(): void {
  // Position inputs
  const posX = document.getElementById('posX');
  if (posX) {
    posX.addEventListener('change', (e) => {
      if (selectedPosition && scene?.currentLocalePositions[selectedPosition]) {
        scene.currentLocalePositions[selectedPosition].x = parseInt((e.target as HTMLInputElement).value);
        positionsDirty = true;
        setUnsavedChanges(true);
        scene.renderSpawnPoints(currentLocaleName);
      }
    });
  }
  
  const posY = document.getElementById('posY');
  if (posY) {
    posY.addEventListener('change', (e) => {
      if (selectedPosition && scene?.currentLocalePositions[selectedPosition]) {
        scene.currentLocalePositions[selectedPosition].y = parseInt((e.target as HTMLInputElement).value);
        positionsDirty = true;
        setUnsavedChanges(true);
        scene.renderSpawnPoints(currentLocaleName);
      }
    });
  }
  
  const posLabel = document.getElementById('posLabel');
  if (posLabel) {
    posLabel.addEventListener('change', (e) => {
      if (selectedPosition && scene?.currentLocalePositions[selectedPosition]) {
        scene.currentLocalePositions[selectedPosition].label = (e.target as HTMLInputElement).value || undefined;
        positionsDirty = true;
        setUnsavedChanges(true);
      }
    });
  }
  
  const deletePositionBtn = document.getElementById('deletePositionBtn');
  if (deletePositionBtn) {
    deletePositionBtn.addEventListener('click', () => {
      if (selectedPosition && scene && confirm(`Delete position "${selectedPosition}"?`)) {
        scene.deletePosition(selectedPosition);
      }
    });
  }
  
  // Prop inputs
  const propX = document.getElementById('propX');
  if (propX) {
    propX.addEventListener('change', (e) => {
      if (selectedProp) {
        saveState('propX');
        selectedProp.x = parseInt((e.target as HTMLInputElement).value);
        selectedProp.sprite.x = selectedProp.x;
      }
    });
  }
  
  const propY = document.getElementById('propY');
  if (propY) {
    propY.addEventListener('change', (e) => {
      if (selectedProp) {
        saveState('propY');
        const oldY = selectedProp.y;
        selectedProp.y = parseInt((e.target as HTMLInputElement).value);
        selectedProp.sprite.y = selectedProp.y;
        if (selectedProp.depth === oldY) {
          selectedProp.depth = selectedProp.y;
          selectedProp.sprite.setDepth(selectedProp.y);
          updatePropertyPanel();
        }
      }
    });
  }
  
  const propScale = document.getElementById('propScale');
  if (propScale) {
    propScale.addEventListener('mousedown', () => {
      if (selectedProp) saveState('scale');
    });
    propScale.addEventListener('input', (e) => {
      if (selectedProp) {
        selectedProp.scale = parseFloat((e.target as HTMLInputElement).value);
        selectedProp.sprite.setScale(selectedProp.scale);
        setText('scaleValue', selectedProp.scale.toFixed(1));
      }
    });
  }
  
  const propDepth = document.getElementById('propDepth');
  if (propDepth) {
    propDepth.addEventListener('change', (e) => {
      if (selectedProp) {
        saveState('depth');
        selectedProp.depth = parseInt((e.target as HTMLInputElement).value);
        selectedProp.sprite.setDepth(selectedProp.depth);
      }
    });
  }
  
  const propFlipX = document.getElementById('propFlipX');
  if (propFlipX) {
    propFlipX.addEventListener('change', (e) => {
      if (selectedProp) {
        saveState('flipX');
        selectedProp.flipX = (e.target as HTMLInputElement).checked;
        selectedProp.sprite.setFlipX(selectedProp.flipX);
      }
    });
  }
  
  const rotateLeftBtn = document.getElementById('rotateLeftBtn');
  if (rotateLeftBtn) {
    rotateLeftBtn.addEventListener('click', () => {
      if (selectedProp) {
        saveState('rotate');
        selectedProp.angle = ((selectedProp.angle || 0) - 90 + 360) % 360;
        selectedProp.sprite.setAngle(selectedProp.angle);
        setText('angleValue', selectedProp.angle + '°');
      }
    });
  }
  
  const rotateRightBtn = document.getElementById('rotateRightBtn');
  if (rotateRightBtn) {
    rotateRightBtn.addEventListener('click', () => {
      if (selectedProp) {
        saveState('rotate');
        selectedProp.angle = ((selectedProp.angle || 0) + 90) % 360;
        selectedProp.sprite.setAngle(selectedProp.angle);
        setText('angleValue', selectedProp.angle + '°');
      }
    });
  }
  
  const bringFrontBtn = document.getElementById('bringFrontBtn');
  if (bringFrontBtn) {
    bringFrontBtn.addEventListener('click', () => {
      if (selectedProp) {
        saveState('bringFront');
        const maxDepth = Math.max(...props.map(p => p.depth));
        selectedProp.depth = maxDepth + 1;
        selectedProp.sprite.setDepth(selectedProp.depth);
        updatePropertyPanel();
      }
    });
  }
  
  const sendBackBtn = document.getElementById('sendBackBtn');
  if (sendBackBtn) {
    sendBackBtn.addEventListener('click', () => {
      if (selectedProp) {
        saveState('sendBack');
        selectedProp.depth = -100;
        selectedProp.sprite.setDepth(selectedProp.depth);
        updatePropertyPanel();
      }
    });
  }
  
  const duplicateBtn = document.getElementById('duplicateBtn');
  if (duplicateBtn) {
    duplicateBtn.addEventListener('click', () => {
      if (selectedProp && scene) {
        const newProp = scene.addProp(
          selectedProp.key,
          selectedProp.x + 20,
          selectedProp.y + 20,
          selectedProp.scale,
          selectedProp.depth,
          selectedProp.flipX,
          selectedProp.angle || 0
        );
        selectedProp = newProp;
        updatePropertyPanel();
      }
    });
  }
  
  // Locale color inputs
  const localeWallColor = document.getElementById('localeWallColor');
  const localeFloorColor = document.getElementById('localeFloorColor');
  const localeBgColor = document.getElementById('localeBgColor');
  
  const updateLocaleColors = () => {
    if (!scene) return;
    const wallColor = hexToInt((localeWallColor as HTMLInputElement)?.value || '#8b7355');
    const floorColor = hexToInt((localeFloorColor as HTMLInputElement)?.value || '#deb887');
    const bgColor = hexToInt((localeBgColor as HTMLInputElement)?.value || '#654321');
    scene.updateLocaleColors(wallColor, floorColor, bgColor);
  };
  
  if (localeWallColor) {
    localeWallColor.addEventListener('input', updateLocaleColors);
  }
  if (localeFloorColor) {
    localeFloorColor.addEventListener('input', updateLocaleColors);
  }
  if (localeBgColor) {
    localeBgColor.addEventListener('input', updateLocaleColors);
  }
}

function setupEmbeddedMode(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbedded = urlParams.get('embedded') === 'true';
  
  if (isEmbedded) {
    // Set module-level flag for embedded mode
    isEmbeddedMode = true;
    
    // Hide file operations
    setDisplay('localeSelectGroup', 'none');
    setDisplay('localeSelectDivider', 'none');
    setDisplay('fileOpsGroup', 'none');
    
    document.body.style.background = '#1a1a2e';
    
    // Notify parent
    window.parent.postMessage({ type: 'editor:ready' }, '*');
    
    // Listen for parent commands
    window.addEventListener('message', async (event: MessageEvent<HostMessage>) => {
      const data = event.data;
      if (!data || typeof data !== 'object' || !('type' in data)) return;
      
      switch (data.type) {
        case 'host:loadLocale':
          if (data.localeName && scene) {
            await scene.loadLocaleProps(data.localeName);
          }
          break;
          
        case 'host:loadYaml':
          if (data.yaml && scene) {
            try {
              const localeData = jsyaml.load(data.yaml) as LocaleData;
              const localeName = localeData?.locale?.name?.toLowerCase().replace(/\s+/g, '_') || 'custom';
              currentLocaleName = localeName;
              currentLocaleData = localeData;
              
              scene.currentLocalePositions = localeData?.locale?.positions || {};
              scene.drawBackground(localeData);
              scene.clearAllProps();
              
              const localeProps = localeData?.locale?.props || [];
              for (const propData of localeProps) {
                scene.addProp(propData.key, propData.x, propData.y, propData.scale || 2, propData.depth, propData.flipX, propData.angle || 0, true);
              }
              
              scene.loadWalls(localeData?.locale?.walls || []);
              scene.renderSpawnPoints(localeName);
              updatePropCount();
              
              // Update the locale colors panel
              updateLocaleSettingsPanel(localeData);
            } catch (error) {
              console.error('Failed to load YAML:', error);
              window.parent.postMessage({ type: 'editor:error', error: (error as Error).message }, '*');
            }
          }
          break;
          
        case 'host:createLocale':
          const nameInput = document.getElementById('newLocaleName') as HTMLInputElement;
          const widthInput = document.getElementById('newLocaleWidth') as HTMLInputElement;
          const heightInput = document.getElementById('newLocaleHeight') as HTMLInputElement;
          const wallColorInput = document.getElementById('newLocaleWallColor') as HTMLInputElement;
          const floorColorInput = document.getElementById('newLocaleFloorColor') as HTMLInputElement;
          const bgColorInput = document.getElementById('newLocaleBgColor') as HTMLInputElement;
          if (nameInput) nameInput.value = data.name || '';
          if (widthInput) widthInput.value = String(data.width || 800);
          if (heightInput) heightInput.value = String(data.height || 450);
          if (wallColorInput && data.wallColor) wallColorInput.value = data.wallColor;
          if (floorColorInput && data.floorColor) floorColorInput.value = data.floorColor;
          if (bgColorInput && data.bgColor) bgColorInput.value = data.bgColor;
          document.getElementById('createLocaleBtn')?.click();
          break;
        
        case 'host:listLocales':
          // Return list of built-in locales
          const builtInLocales = [
            { id: 'coffee_shop', name: 'Coffee Shop' },
            { id: 'office', name: 'Office' },
            { id: 'park', name: 'Park' },
            { id: 'treasure_room', name: 'Treasure Room' },
            { id: 'office_floor', name: 'Office Floor (large)' },
            { id: 'jasper_block', name: 'Jasper Block (large)' },
          ];
          window.parent.postMessage({
            type: 'editor:response',
            requestId: data.requestId,
            data: builtInLocales
          }, '*');
          break;
        
        case 'host:updateColors':
          if (scene) {
            const wallColor = data.wallColor ? hexToInt(data.wallColor) : (currentLocaleData?.locale?.wallColor ?? 0x8b7355);
            const floorColor = data.floorColor ? hexToInt(data.floorColor) : (currentLocaleData?.locale?.floorColor ?? 0xdeb887);
            const bgColor = data.bgColor ? hexToInt(data.bgColor) : (currentLocaleData?.locale?.background ?? 0x654321);
            scene.updateLocaleColors(wallColor, floorColor, bgColor);
            // Also update the color inputs
            updateLocaleSettingsPanel(currentLocaleData);
          }
          break;
        
        case 'host:getLocaleColors':
          window.parent.postMessage({
            type: 'editor:response',
            requestId: data.requestId,
            data: {
              wallColor: intToHex(currentLocaleData?.locale?.wallColor ?? 0x8b7355),
              floorColor: intToHex(currentLocaleData?.locale?.floorColor ?? 0xdeb887),
              bgColor: intToHex(currentLocaleData?.locale?.background ?? 0x654321),
            }
          }, '*');
          break;
          
        case 'host:getYaml':
          const yaml = generateFullYaml();
          window.parent.postMessage({
            type: 'editor:response',
            requestId: data.requestId,
            data: yaml
          }, '*');
          break;
          
        case 'host:getLocaleName':
          window.parent.postMessage({
            type: 'editor:response',
            requestId: data.requestId,
            data: currentLocaleName
          }, '*');
          break;
      }
    });
  }
}

// Show demo banner if in demo mode
function showDemoBannerIfNeeded(): void {
  if (!isDemoMode()) return;
  
  const banner = document.getElementById('demoBanner');
  if (banner) {
    banner.classList.add('visible');
  }
  
  // Also update the save button tooltip
  const saveBtn = document.getElementById('saveBtn');
  if (saveBtn) {
    saveBtn.title = 'Demo mode: Changes saved to browser only';
  }
}

// Auto-init on DOMContentLoaded
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    initEditor().catch(console.error);
    showDemoBannerIfNeeded();
  });
}

// Export for use in other modules
export {
  EditorScene,
  generateFullYaml,
  saveToBackend,
  props,
  walls,
  currentLocaleName,
  currentLocaleData,
  scene,
  // EditorState instance (centralized state management)
  state,
  // State flags (exported to satisfy noUnusedLocals, may be used by extensions)
  game,
  isDragging,
  positionsDirty,
  hasUnsavedChanges,
};

